import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { CRM_MAX_FIELD_LENGTH } from '../../../common/config';
import { FileUpload } from '../../../components/FileUpload/FileUpload';
import { TemplateFlexGridContainer } from '../../../components/Grid';
import { Heading } from '../../../components/Heading';
import { InfoText } from '../../../components/InfoText';
import { InputGroup } from '../../../components/InputGroup';
import { Textarea } from '../../../components/Textarea';
import { noOp } from '../../../lib/utils';
import { AttachedFile, StepProps } from '../../../types';
import { useBriefingState } from '../BriefingState';

export const StepAdditionalInfo: FC<StepProps> = ({
  mandatoryFields,
  onValidate = noOp,
}) => {
  const { t } = useTranslation();
  const [
    { additionalInfoFiles, additionalInfo },
    { setAdditionalInfo, setAdditionalInfoFiles },
  ] = useBriefingState();
  const dispatch = useDispatch();

  const [isAdditionalInfoValid, setIsAdditionalInfoValid] = useState(true);
  const isStepValid = useMemo(
    () => isAdditionalInfoValid,
    [isAdditionalInfoValid],
  );
  useEffect(() => onValidate(isStepValid), [isStepValid, onValidate]);

  const onAdditionalInfoChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    dispatch(setAdditionalInfo(event.target.value));
  };

  const onFilesChange = useCallback(
    (files: AttachedFile[]) => {
      dispatch(setAdditionalInfoFiles(files));
    },
    [dispatch, setAdditionalInfoFiles],
  );
  const [initialFiles] = useState(additionalInfoFiles);
  const [initialAdditionalInfo] = useState(additionalInfo);
  return (
    <TemplateFlexGridContainer.BriefingStep>
      <Heading
        level="h1"
        margin={0}
        text={t(`topics:additional-info:heading:general`)}
        optionalField={mandatoryFields['additional-info']?.length === 0}
      />
      <TemplateFlexGridContainer.BriefingStepBody>
        <div className="w-full md:w-8/12">
          <TemplateFlexGridContainer.BriefingStepSection>
            <div className="w-full">
              <InputGroup label={t('labels:additionalInfo')}>
                <div className="w-full">
                  <Textarea
                    {...(initialAdditionalInfo
                      ? { initialValue: initialAdditionalInfo }
                      : {})}
                    rows={5}
                    onChange={onAdditionalInfoChange}
                    onValidate={setIsAdditionalInfoValid}
                    maxLength={CRM_MAX_FIELD_LENGTH}
                  />
                </div>
              </InputGroup>
            </div>
            {/* Container div to stop iPad button height issue */}
            <div>
              <FileUpload
                label={t('labels:addAttachment')}
                {...(initialFiles ? { initialFiles: initialFiles } : {})}
                onChange={onFilesChange}
              />
            </div>
            <div className="mt-7 -mb-7">
              <InfoText
                color="darker"
                variant="small"
                text={t('components:fileUpload:privacy')}
              />
            </div>
          </TemplateFlexGridContainer.BriefingStepSection>
        </div>
      </TemplateFlexGridContainer.BriefingStepBody>
    </TemplateFlexGridContainer.BriefingStep>
  );
};
